import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import { Upload, FileSpreadsheet, Trash2, Eye, X, Download, ChevronDown, ChevronUp, Check } from 'lucide-react';

const API = '/api';

export default function Contacts() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewList, setViewList] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);

  // Preview / Mapping state
  const [previewData, setPreviewData] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [mapping, setMapping] = useState({ phone: '', name: '', prefix: '' });
  const [listName, setListName] = useState('');
  const [defaultPrefix, setDefaultPrefix] = useState('');

  const fetchLists = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/contact-lists`);
      setLists(res.data);
    } catch (e) {
      toast.error('Error al cargar listas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  const downloadTemplate = () => {
    const a = document.createElement('a');
    a.href = `${API}/contacts/template`;
    a.download = 'plantilla_contactos.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Step 1: File dropped → preview
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    setPendingFile(file);
    setListName(file.name.replace(/\.[^/.]+$/, ''));

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/contact-lists/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreviewData(res.data);
      setMapping({
        phone: res.data.auto_detected.phone || '',
        name: res.data.auto_detected.name || '',
        prefix: res.data.auto_detected.prefix || '',
      });
    } catch (e) {
      const detail = e.response?.data?.detail || 'Error al leer el archivo';
      toast.error(detail);
      setPendingFile(null);
    }
  }, []);

  // Step 2: Confirm mapping → upload
  const confirmUpload = async () => {
    if (!pendingFile || !mapping.phone) {
      toast.error('Selecciona la columna de teléfono');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', pendingFile);
      const params = new URLSearchParams();
      params.append('list_name', listName || 'Importado');
      if (defaultPrefix) params.append('default_prefix', defaultPrefix);
      params.append('phone_col', mapping.phone);
      if (mapping.name) params.append('name_col', mapping.name);
      if (mapping.prefix) params.append('prefix_col', mapping.prefix);

      const res = await axios.post(`${API}/contact-lists/upload?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${res.data.total_contacts} contactos importados correctamente`);
      cancelPreview();
      fetchLists();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Error al importar contactos';
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const cancelPreview = () => {
    setPreviewData(null);
    setPendingFile(null);
    setMapping({ phone: '', name: '', prefix: '' });
    setListName('');
    setDefaultPrefix('');
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'text/plain': ['.txt'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
    disabled: !!previewData
  });

  const deleteList = async (id) => {
    try {
      await axios.delete(`${API}/contact-lists/${id}`);
      toast.success('Lista eliminada');
      fetchLists();
      if (viewList === id) setViewList(null);
    } catch (e) {
      toast.error('Error al eliminar lista');
    }
  };

  const viewContacts = async (listId) => {
    if (viewList === listId) {
      setViewList(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/contact-lists/${listId}/contacts?limit=200`);
      setContacts(res.data.contacts);
      setContactsTotal(res.data.total);
      setViewList(listId);
    } catch (e) {
      toast.error('Error al cargar contactos');
    }
  };

  // Detect all custom_var keys across contacts
  const customVarKeys = contacts.length > 0
    ? [...new Set(contacts.flatMap(c => Object.keys(c.custom_vars || {})))]
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-heading font-bold tracking-tight text-white">Contactos</h1>
          <p className="text-xs text-zinc-500 mt-0.5 font-mono">{lists.length} listas</p>
        </div>
        <button
          data-testid="download-template-btn"
          onClick={downloadTemplate}
          className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-md text-xs text-zinc-300 transition-colors duration-200"
        >
          <Download className="w-3.5 h-3.5" />
          Descargar Plantilla
        </button>
      </div>

      {/* Drop zone - only when no preview active */}
      {!previewData && (
        <div
          {...getRootProps()}
          data-testid="file-dropzone"
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-300 ${
            isDragActive ? 'border-blue-500 bg-blue-500/5' : 'border-zinc-800 hover:border-zinc-700 bg-zinc-900/50'
          }`}
        >
          <input {...getInputProps()} />
          <Upload className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
          <p className="text-sm text-zinc-400">Arrastra un archivo CSV o Excel aquí</p>
          <p className="text-xs text-zinc-600 mt-1">o haz clic para seleccionar</p>
          <p className="text-[10px] font-mono text-zinc-700 mt-2">CSV, XLSX, XLS o TXT</p>
        </div>
      )}

      {/* Preview & Column Mapping */}
      {previewData && (
        <div className="bg-zinc-900 border border-blue-500/30 rounded-lg p-5 space-y-5 animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-white">Configurar Importación</h3>
              <p className="text-xs text-zinc-500 mt-0.5">
                {previewData.total_rows} filas detectadas · {previewData.headers.length} columnas: <span className="text-zinc-400 font-mono">{previewData.headers.join(', ')}</span>
              </p>
            </div>
            <button onClick={cancelPreview} className="p-1.5 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Column Mapping */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">
                Columna Teléfono <span className="text-red-400">*</span>
              </label>
              <select
                data-testid="map-phone-select"
                value={mapping.phone}
                onChange={e => setMapping(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full bg-zinc-950 border border-zinc-700 focus:border-blue-500 rounded-md px-3 py-2 text-sm text-white outline-none"
              >
                <option value="">-- Seleccionar --</option>
                {previewData.headers.map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Columna Nombre</label>
              <select
                data-testid="map-name-select"
                value={mapping.name}
                onChange={e => setMapping(prev => ({ ...prev, name: e.target.value }))}
                className="w-full bg-zinc-950 border border-zinc-700 focus:border-blue-500 rounded-md px-3 py-2 text-sm text-white outline-none"
              >
                <option value="">-- Sin nombre --</option>
                {previewData.headers.map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Columna Prefijo</label>
              <select
                data-testid="map-prefix-select"
                value={mapping.prefix}
                onChange={e => setMapping(prev => ({ ...prev, prefix: e.target.value }))}
                className="w-full bg-zinc-950 border border-zinc-700 focus:border-blue-500 rounded-md px-3 py-2 text-sm text-white outline-none"
              >
                <option value="">-- Sin prefijo en columna --</option>
                {previewData.headers.map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">
                Código País por Defecto
              </label>
              <div className="flex items-center gap-1.5">
                <span className="text-zinc-500 text-sm">+</span>
                <input
                  data-testid="default-prefix-input"
                  value={defaultPrefix}
                  onChange={e => setDefaultPrefix(e.target.value.replace(/[^0-9]/g, ''))}
                  className="w-full bg-zinc-950 border border-zinc-700 focus:border-blue-500 rounded-md px-3 py-2 text-sm text-white outline-none"
                  placeholder="549, 34, 1..."
                  maxLength={5}
                />
              </div>
              <p className="text-[9px] text-zinc-600 mt-1">Para números sin prefijo</p>
            </div>
          </div>

          {/* List name */}
          <div className="max-w-md">
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Nombre de la Lista</label>
            <input
              data-testid="list-name-input"
              value={listName}
              onChange={e => setListName(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-700 focus:border-blue-500 rounded-md px-3 py-2 text-sm text-white outline-none"
              placeholder="Nombre para la lista"
            />
          </div>

          {/* Sample data preview table */}
          <div>
            <h4 className="text-xs font-semibold text-zinc-400 mb-2">Vista previa (primeras {previewData.sample_rows.length} filas)</h4>
            <div className="overflow-x-auto border border-zinc-800 rounded-md">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-zinc-800/50 text-zinc-400 uppercase tracking-wider">
                    {previewData.headers.map(h => (
                      <th key={h} className={`py-2 px-3 text-left whitespace-nowrap ${
                        h === mapping.phone ? 'text-blue-400 border-b-2 border-blue-500' :
                        h === mapping.name ? 'text-emerald-400 border-b-2 border-emerald-500' :
                        h === mapping.prefix ? 'text-amber-400 border-b-2 border-amber-500' : ''
                      }`}>
                        {h}
                        {h === mapping.phone && <span className="ml-1 text-[9px] normal-case">(tel)</span>}
                        {h === mapping.name && <span className="ml-1 text-[9px] normal-case">(nombre)</span>}
                        {h === mapping.prefix && <span className="ml-1 text-[9px] normal-case">(prefijo)</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.sample_rows.map((row, i) => (
                    <tr key={i} className="border-t border-zinc-800/50 hover:bg-zinc-800/20">
                      {previewData.headers.map(h => (
                        <td key={h} className={`py-1.5 px-3 whitespace-nowrap text-zinc-300 ${
                          h === mapping.phone ? 'text-blue-300 font-mono' :
                          h === mapping.name ? 'text-emerald-300' :
                          h === mapping.prefix ? 'text-amber-300 font-mono' : ''
                        }`}>
                          {row[h] || ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3">
            <button
              data-testid="confirm-import-btn"
              onClick={confirmUpload}
              disabled={!mapping.phone || uploading}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-md text-sm font-medium text-white transition-colors duration-200"
            >
              {uploading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              {uploading ? 'Importando...' : `Importar ${previewData.total_rows} contactos`}
            </button>
            <button
              data-testid="cancel-import-btn"
              onClick={cancelPreview}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-md text-sm text-zinc-400 transition-colors duration-200"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Contact Lists Table */}
      <div className="bg-zinc-900 border border-white/5 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-zinc-900/50 text-xs uppercase tracking-wider text-zinc-500 font-bold border-b border-white/5">
              <th className="py-3 px-4 text-left">Lista</th>
              <th className="py-3 px-4 text-left">Archivo</th>
              <th className="py-3 px-4 text-center">Contactos</th>
              <th className="py-3 px-4 text-left">Fecha</th>
              <th className="py-3 px-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {lists.map((l, i) => (
              <tr
                key={l.id}
                data-testid={`contact-list-row-${i}`}
                className={`hover:bg-white/[0.02] transition-colors duration-200 border-b border-white/5 last:border-0 ${
                  viewList === l.id ? 'bg-blue-500/5' : ''
                }`}
              >
                <td className="py-3 px-4 text-sm text-zinc-200 font-medium">{l.name}</td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-zinc-600" />
                    <span className="text-xs font-mono text-zinc-500">{l.filename || '-'}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-center text-sm font-mono text-zinc-300">{l.total_contacts ?? l.count ?? '-'}</td>
                <td className="py-3 px-4 text-xs text-zinc-500">
                  {new Date(l.created_at).toLocaleDateString('es-ES')}
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-1 justify-end">
                    <button
                      data-testid={`view-contacts-${i}`}
                      onClick={() => viewContacts(l.id)}
                      className={`p-1.5 rounded transition-colors duration-200 ${
                        viewList === l.id ? 'bg-blue-500/20 text-blue-400' : 'hover:bg-zinc-800 text-zinc-400 hover:text-white'
                      }`}
                    >
                      {viewList === l.id ? <ChevronUp className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      data-testid={`delete-list-${i}`}
                      onClick={() => deleteList(l.id)}
                      className="p-1.5 hover:bg-red-500/10 rounded transition-colors duration-200 text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {lists.length === 0 && (
          <div className="text-center py-12">
            <FileSpreadsheet className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">No hay listas de contactos</p>
          </div>
        )}
      </div>

      {/* Contacts Detail View */}
      {viewList && (
        <div className="bg-zinc-900 border border-zinc-700/50 rounded-lg p-5 animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-zinc-400">
              Contactos ({contactsTotal})
            </h3>
            <button
              data-testid="close-contacts-view"
              onClick={() => setViewList(null)}
              className="p-1 hover:bg-zinc-800 rounded transition-colors duration-200 text-zinc-400"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {contacts.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-zinc-500 text-sm">No se encontraron contactos en esta lista</p>
              <p className="text-zinc-600 text-xs mt-1">Es posible que la lista sea antigua y los contactos no estén vinculados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold border-b border-white/5">
                    <th className="py-2 px-3 text-left">#</th>
                    <th className="py-2 px-3 text-left">Teléfono</th>
                    <th className="py-2 px-3 text-left">Nombre</th>
                    {customVarKeys.map(k => (
                      <th key={k} className="py-2 px-3 text-left">{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c, i) => (
                    <tr key={c.id || i} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors duration-100">
                      <td className="py-1.5 px-3 text-xs font-mono text-zinc-600">{i + 1}</td>
                      <td className="py-1.5 px-3 text-xs font-mono text-blue-300">{c.phone}</td>
                      <td className="py-1.5 px-3 text-xs text-zinc-300">{c.name || '-'}</td>
                      {customVarKeys.map(k => (
                        <td key={k} className="py-1.5 px-3 text-xs text-zinc-400 max-w-[200px] truncate">
                          {c.custom_vars?.[k] || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {contactsTotal > contacts.length && (
                <p className="text-center text-xs text-zinc-600 py-3">
                  Mostrando {contacts.length} de {contactsTotal} contactos
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
